import { Link, Stack, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import { DataGridPro, GridColDef, GridValueFormatterParams } from '@mui/x-data-grid-pro';
import PropTypes from 'prop-types';
import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PopupComponent } from 'src/components/common/PopupComponent';
import { AppState, store, useAppSelector } from 'src/store';
import { setDocumentDetail } from 'src/store/documentDetail';
import { setSnackbarMessage } from 'src/store/snackbarMessage';
import { usePreferedDateFormat } from 'src/util/DateFormatter';
import DocManagementService from '../documentUpload/service/DocManagementService';
import { CustomPagination } from 'src/util/DataTableFooter';
import { Loader } from 'src/components/common/Loader';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2)
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1)
  }
}));
export interface IVersion {
  openVersionHistory?: any;
  setOpenVersionHistory?: any;
  setAnchorEl: any;
  setFileId: any;
  setDataGridComponent: any;
  setDocumentToDisplay1: any;
  fromVersionHistory?: any;
}

const VersionHistory: FC<any> = ({
  openVersionHistory,
  setOpenVersionHistory,
  setAnchorEl,
  setFileId,
  setDataGridComponent,
  setDocumentToDisplay1,
  fromVersionHistory
}: IVersion) => {
  const dispatch = useDispatch();
  const docService = new DocManagementService();
  const documentDetail: any = useSelector((state: AppState) => state.documentDetail);
  const [versionHistoryDetails, setVersionHistoryDetails] = useState<any>([]);
  const [pageSize, setPageSize] = React.useState<number>(10);
  const { preferedDateFormat } = usePreferedDateFormat();
  const [loading, setLoading] = useState<any>(false);
  const isfLevel = documentDetail?.data[0]?.isf_doc_level;
  const selectedStudy = useSelector((state: AppState) => state.searchHierarchyDetails?.data[0]?.Study);
  const selectedCountry = useSelector((state: AppState) => state.searchHierarchyDetails?.data[0]?.Country);
  const selectedSite = useSelector((state: AppState) => state.searchHierarchyDetails?.data[0]?.Site);
  const entity = documentDetail?.data[0]?.isf_document_name;
  const [loaderStatus, setLoaderStatus] = useState(false);

  const getVersionHistory = async () => {
    setLoading(true);
    const result = await docService.getVersionHistory(
      documentDetail.data[0]?.isf_document_id,
      selectedStudy,
      selectedCountry,
      selectedSite,
      isfLevel,
      process.env.REACT_APP_NAME,
      'DocumentDetails',
      entity
    );
    const updated = result.data.map((res: any) => {
      return { id: res.document_version.document_id, fileId: res.file_master_id, ...res.document_version };
    });
    setVersionHistoryDetails(updated);
    setLoading(false);
  };

  useEffect(() => {
    getVersionHistory();
  }, []);

  const getVesionBasedDocumentDetails = async (vd: any) => {
    const site = vd.isf_doc_level === 'Site' ? vd.isf_doc_site : '--';
    const country = vd.isf_doc_level === 'Country' || vd.isf_doc_level === 'Site' ? vd.isf_doc_country : '--';
    const res: any = await docService.getMasterDocumentdetails(
      vd.isf_doc_study,
      vd.isf_document_id,
      vd.isf_doc_version_number,
      country,
      site,
      vd.isf_doc_level,
      process.env.REACT_APP_NAME,
      'DocumentDetails',
      vd.isf_document_name
    );
    if (res.data) {
      const latestVersionDetails = versionHistoryDetails[0];

      const docDetailsObj: any = [
        {
          ...documentDetail.data[0],
          fmId: res.data.file_master.id,
          isf_doc_latest_version: latestVersionDetails?.isf_doc_version_number,
          fileId: res.data.file_master.id,
          isf_doc_version_number: res.data.document_data.isf_doc_version_number,
          isf_document_name: res.data.document_data.isf_document_name,
          isf_doc_upload_date: res.data.file_master.isf_doc_upload_date,
          isf_document_id: res.data.document_data.isf_document_id,
          isf_doc_level: res.data.document_data.isf_doc_level,
          isf_doc_study: res.data.document_data.isf_doc_study,
          isf_doc_viewer_department: res.data.document_data.isf_doc_viewer_department,
          isf_artifact_number: res.data.document_data.isf_artifact_number,
          isf_artifact_name: res.data.document_data.isf_artifact_name,
          isf_doc_type: res.data.document_data.isf_doc_type,
          isf_doc_subtype: res.data.document_data.isf_doc_subtype,
          isf_doc_milestone: res.data.document_data.isf_doc_milestone,
          isf_doc_created_by: res.data.document_data.isf_doc_viewer_created_by,
          isf_doc_status: res.data.document_data.isf_doc_status,
          isf_raw_file_name: res.data.file_master.isf_raw_file_name,
          isf_doc_size: res.data.file_master.isf_doc_size,
          docFormat: res.data.file_master.isf_doc_type,
          isf_doc_viewer_pages: res.data.file_master.isf_doc_viewer_pages,
          isf_doc_viewer_red_certified_copy: res.data.document_data.isf_doc_viewer_red_certified_copy,
          isf_doc_viewer_doc_phi_redacted: res.data.document_data.isf_doc_viewer_doc_phi_redacted,
          isf_doc_viewer_includes_phi: res.data.document_data.isf_doc_viewer_includes_phi,
          isf_doc_viewer_last_modified_by: res.data.document_data?.isf_doc_viewer_last_modified_by
        }
      ];
      setDocumentDetail(dispatch, docDetailsObj);
      return docDetailsObj;
    } else {
      store.dispatch(setSnackbarMessage('Error in getting Version History data', 'error'));
    }
  };

  const gridFunc = async (versionDetails: any) => {
    setLoaderStatus(true);
    setFileId(versionDetails.fileId);
    const resultOfDoc = await getVesionBasedDocumentDetails(versionDetails);
    setDocumentToDisplay1(resultOfDoc[0]);
    handleClose();
    setDataGridComponent(false);
    fromVersionHistory && fromVersionHistory(true);
    setLoaderStatus(false);
  };

  const columns: GridColDef[] = [
    {
      field: 'isf_doc_version_number',
      headerName: 'Version',
      width: 280,
      renderCell: (params: any) => (
        <Link onClick={() => gridFunc(params.row)}>{params.value!.toFixed(1).toString()}</Link>
      )
    },
    {
      field: 'isf_doc_last_modified_by',
      headerName: 'Name',
      width: 280
    },
    {
      field: 'isf_doc_upload_date',
      headerName: 'Date Modified',
      width: 280,
      valueFormatter: (params: GridValueFormatterParams) => {
        return preferedDateFormat(params.value);
      }
    }
  ];

  const handleClose = () => {
    setOpenVersionHistory(false);
    setAnchorEl(null);
  };

  function TabPanel(props: { [x: string]: any; children: any; value: any; index: any }) {
    const { children, value, index, ...other } = props;

    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`vertical-tabpanel-${index}`}
        aria-labelledby={`vertical-tab-${index}`}
        {...other}
      >
        {value === index && (
          <Box sx={{ p: 3 }}>
            <Typography>{children}</Typography>
          </Box>
        )}
      </div>
    );
  }

  TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired
  };

  return (
    <div>
      <div className="query-modal">
        <PopupComponent aria-labelledby="customized-dialog-title" open={openVersionHistory} maxWidth="md">
          <Loader open={loaderStatus} />
          <DialogContent dividers className="audit-history-modal">
            <div className="audit-card">
              <Grid container spacing={3}>
                <Grid md={12}>
                  <Box sx={{ flexGrow: 1, bgcolor: 'background.paper', display: 'flex', pt: 0 }}>
                    <Grid container>
                      <Grid container>
                        <Grid md={12}>
                          <h2>{`Version History: ${documentDetail.data[0]?.isf_document_name}`}</h2>
                        </Grid>
                      </Grid>
                      <Grid md={12}>
                        <div style={{ height: 360, width: '100%' }}>
                          <DataGridPro
                            rows={versionHistoryDetails}
                            columns={columns}
                            pageSize={pageSize}
                            components={{ Pagination: CustomPagination }}
                            onPageSizeChange={newPageSize => setPageSize(newPageSize)}
                            rowsPerPageOptions={[5, 10, 20]}
                            pagination
                            disableSelectionOnClick
                            initialState={{
                              sorting: {
                                sortModel: [{ field: 'updated_on', sort: 'desc' }]
                              }
                            }}
                            loading={loading}
                          />
                        </div>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
                <Grid item md={12}>
                  <Grid container spacing={2}>
                    <Grid item md={12} display="flex" justifyContent="flex-end">
                      <Stack direction="row" spacing={2}>
                        <Button
                          variant="text"
                          href="#contained-buttons"
                          className="grey-btn"
                          onClick={handleClose}
                          disabled={loading}
                          sx={{ fontSize: '14px' }}
                        >
                          Close
                        </Button>
                      </Stack>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </div>
          </DialogContent>
        </PopupComponent>
      </div>
    </div>
  );
};

export default React.memo(VersionHistory);
